import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import StepUpload from '@/features/imports/StepUpload';
import StepMapping from '@/features/imports/StepMapping';
import StepProgress from '@/features/imports/StepProgress';
import StepImportMode, { ImportMode } from '@/features/imports/StepImportMode';
import UploadProgress from '@/features/imports/UploadProgress';
import {
  createProductImport,
  createAttributeGroupImport,
  createAttributeImport,
  createFamilyImport,
  createBulkImageImport,
  getImportFieldSchema,
  DuplicateStrategy,
  ImportFieldSchemaEntry,
  UploadProgressEvent,
} from '@/services/importService';
import { useImportFieldSchema } from './hooks/useImportFieldSchema';
import { Mapping } from '@/types/import';
import { config } from '@/config/config';
import { createLogger } from '@/lib/logger';
import { captureImportError, captureNetworkError } from '@/lib/errorReporting';
import { useImportTranslations } from '@/hooks/useImportTranslations';
import { useTheme } from '@/contexts/ThemeContext';
import {
  ArrowLeft,
  Info,
  FileText,
  Layers,
  Upload,
  Settings,
  CheckCircle,
  HelpCircle,
} from 'lucide-react';

// Define a type for the current step of the wizard
type WizardStep = 'mode' | 'upload' | 'mapping' | 'uploading' | 'progress';

// Define a type for the structure type to import
type StructureType = 'attribute_groups' | 'attributes' | 'families' | null;

// Create component-specific logger
const uploadWizardLogger = createLogger('UploadWizard');

const UploadWizard = () => {
  // Theme support
  const { actualTheme } = useTheme();

  // State for tracking the current step in the wizard
  const [currentStep, setCurrentStep] = useState<WizardStep>('mode');

  // Import mode selection
  const [importMode, setImportMode] = useState<ImportMode>('products');

  // For structure imports, we need to track which type we're importing
  const [structureType, setStructureType] = useState<StructureType>(null);

  // File and data states
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [fullData, setFullData] = useState<any[]>([]);
  const [mapping, setMapping] = useState<Mapping>({}); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [importId, setImportId] = useState<number | null>(null);
  const [duplicateStrategy, setDuplicateStrategy] = useState<DuplicateStrategy>(
    config.imports.defaults.duplicateStrategy as DuplicateStrategy
  );
  const [attributeHeaderPattern, setAttributeHeaderPattern] = useState<string | null>(null); // eslint-disable-line @typescript-eslint/no-unused-vars

  // Upload progress states
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStartTime, setUploadStartTime] = useState<number | null>(null); // eslint-disable-line @typescript-eslint/no-unused-vars

  // Minimum time to show upload progress (in milliseconds)
  const MIN_UPLOAD_DISPLAY_TIME = 2000; // 2 seconds

  // Bulk image specific states
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [overwriteExisting, setOverwriteExisting] = useState(false); // eslint-disable-line @typescript-eslint/no-unused-vars

  const { toast } = useToast();

  // Initialize translations
  const { getWizardText, getImportError, getUploadError, getSuccessMessage, t, getButtonText } =
    useImportTranslations();

  // Fetch the schema data for different import types
  const { productFieldSchema, attributeGroupSchema, attributeSchema, familySchema } =
    useImportFieldSchema();

  // Fetch attribute header pattern on mount
  useEffect(() => {
    getImportFieldSchema(2)
      .then(response => {
        const schema = response.data;
        if (schema && typeof schema === 'object' && 'attribute_header_pattern' in schema) {
          setAttributeHeaderPattern(schema.attribute_header_pattern || null);
        }
      })
      .catch(error => {
        captureNetworkError(error, {
          url: '/api/imports/field-schema/2',
          method: 'GET',
          component: 'UploadWizard',
        });
        uploadWizardLogger.error(
          'Failed to fetch attribute header pattern',
          {
            error: error.message,
          },
          error
        );
      });
  }, []);

  // Handle import mode selection
  const handleModeSelected = (mode: ImportMode) => {
    setImportMode(mode);
    setCurrentStep('upload');

    // Reset other states when mode changes
    setFile(null);
    setHeaders([]);
    setPreviewData([]);
    setMapping({});
    setImportId(null);

    // For structure imports, we need to select a structure type
    if (mode === 'structure') {
      setStructureType(null);
    }
  };

  // Handle structure type selection (for structure imports)
  const handleStructureTypeSelect = (type: StructureType) => {
    setStructureType(type);
    // Could add additional logic here if needed
  };

  // Get the appropriate field schema based on current mode and structure type
  const getCurrentFieldSchema = (): ImportFieldSchemaEntry[] | null => {
    if (importMode === 'products') {
      return productFieldSchema;
    } else if (importMode === 'structure') {
      // For structure imports, use the appropriate schema based on structure type
      switch (structureType) {
        case 'attribute_groups':
          return attributeGroupSchema;
        case 'attributes':
          return attributeSchema;
        case 'families':
          return familySchema;
        default:
          return null;
      }
    }
    return productFieldSchema;
  };

  // Handle file selection from StepUpload
  const handleFileSelected = useCallback(
    (
      selectedFile: File,
      fileHeaders: string[],
      filePreviewData: any[],
      fileFullData: any[] // Add full data parameter
    ) => {
      uploadWizardLogger.debug('File selected in wizard', {
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        headersCount: fileHeaders?.length || 0,
        previewRowsCount: filePreviewData?.length || 0,
        fullRowsCount: fileFullData?.length || 0, // Log full data count
      });

      setFile(selectedFile);
      setHeaders(fileHeaders || []);
      setPreviewData(filePreviewData || []);
      setFullData(fileFullData || []); // Store full data
      setMapping({});

      // Auto-advance to mapping step for supported imports
      if (importMode === 'products' || structureType) {
        setCurrentStep('mapping');
      }
    },
    [importMode, structureType]
  );

  // Handler for bulk image uploads (no mapping needed)
  const handleBulkImageSelected = (images: File[], zip: File | null, overwrite: boolean) => {
    setImageFiles(images);
    setZipFile(zip);
    setOverwriteExisting(overwrite);
    // Skip mapping step and go directly to processing
    handleBulkImageUpload(images, zip, overwrite);
  };

  // Create upload progress callback
  const handleUploadProgress = (progressEvent: UploadProgressEvent) => {
    setUploadProgress(progressEvent.percentage);
  };

  const handleBulkImageUpload = async (images: File[], zip: File | null, overwrite: boolean) => {
    setCurrentStep('uploading');
    setIsUploading(true);
    setUploadProgress(0);
    const startTime = Date.now();
    setUploadStartTime(startTime);

    try {
      const response = await createBulkImageImport(
        (images?.length || 0) > 0 ? images : undefined,
        zip || undefined,
        overwrite,
        progress => {
          setUploadProgress(progress.percentage);
        }
      );

      setIsUploading(false);

      if (response?.data?.id) {
        setImportId(response.data.id);

        // Calculate how long the upload took
        const uploadDuration = Date.now() - startTime;

        // If upload was very fast, wait a bit to show the progress
        if (uploadDuration < MIN_UPLOAD_DISPLAY_TIME) {
          const waitTime = MIN_UPLOAD_DISPLAY_TIME - uploadDuration;
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }

        setCurrentStep('progress');
      } else {
        const errorInfo = getImportError('startFailed');
        toast({
          title: errorInfo.title,
          description: errorInfo.description,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      setIsUploading(false);
      captureImportError(error as Error, {
        importType: 'bulk_images',
        component: 'UploadWizard',
      });
      uploadWizardLogger.error(
        'Bulk image upload failed',
        {
          imageCount: images?.length || 0,
          hasZip: !!zip,
          error: (error as Error).message,
        },
        error as Error
      );

      // Enhanced error handling for bulk image uploads
      let errorTitle = getImportError('startFailed').title;
      let errorDescription = getImportError('startFailed').description;

      // Parse backend validation errors
      if (error?.response?.data) {
        const backendError = error.response.data;

        // Handle field-specific validation errors (like image validation)
        if (backendError.images && Array.isArray(backendError.images)) {
          const imageErrors = backendError.images;
          errorTitle = getImportError('bulkImages.validationFailed').title;

          if ((imageErrors?.length || 0) === 1) {
            // Single image error - show specific details
            errorDescription = imageErrors[0];
          } else {
            // Multiple image errors - show summary
            errorDescription = t('imports.errors.bulkImages.multipleErrors', {
              count: imageErrors?.length || 0,
              firstError: imageErrors[0],
            });
          }
        }
        // Handle file size errors
        else if (backendError.detail && backendError.detail.includes('too large')) {
          errorTitle = getImportError('bulkImages.fileTooLarge').title;
          errorDescription = backendError.detail;
        }
        // Handle dimension errors
        else if (backendError.detail && backendError.detail.includes('dimensions')) {
          errorTitle = getImportError('bulkImages.dimensionsTooLarge').title;
          errorDescription = backendError.detail;
        }
        // Handle file type errors
        else if (backendError.detail && backendError.detail.includes('invalid type')) {
          errorTitle = getImportError('bulkImages.invalidFileType').title;
          errorDescription = backendError.detail;
        }
        // Handle ZIP errors
        else if (backendError.zip_file && Array.isArray(backendError.zip_file)) {
          errorTitle = getImportError('bulkImages.zipError').title;
          errorDescription = backendError.zip_file[0];
        }
        // Handle general validation errors
        else if (backendError.detail) {
          errorDescription = backendError.detail;
        }
        // Handle structured errors
        else if (backendError.non_field_errors && Array.isArray(backendError.non_field_errors)) {
          errorDescription = backendError.non_field_errors[0];
        }
      }

      toast({
        title: errorTitle,
        description: errorDescription,
        variant: 'destructive',
      });
    }
  };

  const handleMappingComplete = async (mappingData: Mapping) => {
    if (!file) {
      const errorInfo = getUploadError('noFileSelected');
      toast({
        title: errorInfo.title,
        description: errorInfo.description,
        variant: 'destructive',
      });
      return;
    }

    setMapping(mappingData);
    setCurrentStep('uploading');
    setIsUploading(true);
    setUploadProgress(0);
    const startTime = Date.now();
    setUploadStartTime(startTime);

    try {
      let response;

      // Call the appropriate import function based on mode and structure type
      if (importMode === 'products') {
        response = await createProductImport(
          file,
          mappingData,
          duplicateStrategy,
          handleUploadProgress
        );
      } else if (importMode === 'structure') {
        switch (structureType) {
          case 'attribute_groups':
            response = await createAttributeGroupImport(file, mappingData, handleUploadProgress);
            break;
          case 'attributes':
            response = await createAttributeImport(file, mappingData, handleUploadProgress);
            break;
          case 'families':
            response = await createFamilyImport(file, mappingData, handleUploadProgress);
            break;
          default:
            throw new Error('Structure type not selected');
        }
      } else {
        throw new Error('Invalid import mode');
      }

      setIsUploading(false);

      if (response?.data?.id) {
        setImportId(response.data.id);

        // Calculate how long the upload took
        const uploadDuration = Date.now() - startTime;

        // If upload was very fast, wait a bit to show the progress
        if (uploadDuration < MIN_UPLOAD_DISPLAY_TIME) {
          await new Promise(resolve =>
            setTimeout(resolve, MIN_UPLOAD_DISPLAY_TIME - uploadDuration)
          );
        }

        setCurrentStep('progress');
      } else {
        const errorInfo = getImportError('startFailed');
        toast({
          title: errorInfo.title,
          description: errorInfo.description,
          variant: 'destructive',
        });
      }
    } catch (error) {
      setIsUploading(false);
      captureImportError(error as Error, {
        importType: importMode,
        component: 'UploadWizard',
      });
      uploadWizardLogger.error(
        'Import upload failed',
        {
          importMode,
          structureType,
          fileName: file.name,
          fileSize: file.size,
          error: (error as Error).message,
        },
        error as Error
      );

      const errorInfo = getImportError('startFailed');
      toast({
        title: errorInfo.title,
        description: errorInfo.description,
        variant: 'destructive',
      });
    }
  };

  // Get the title and description based on the current import mode
  const getImportTitle = () => {
    switch (importMode) {
      case 'products':
        return getWizardText('title', 'products');
      case 'structure':
        return getWizardText('title', 'structure');
      case 'bulk_images':
        return getWizardText('title', 'bulk_images');
      default:
        return getWizardText('title', 'default');
    }
  };

  const getImportDescription = () => {
    switch (importMode) {
      case 'products':
        return getWizardText('description', 'products');
      case 'structure':
        return getWizardText('description', 'structure');
      case 'bulk_images':
        return getWizardText('description', 'bulk_images');
      default:
        return getWizardText('description', 'default');
    }
  };

  const handleDuplicateStrategyChange = (strategy: DuplicateStrategy) => {
    setDuplicateStrategy(strategy);
  };

  const handleGoBackToModeSelection = () => {
    setCurrentStep('mode');
    setImportMode('products');
    setStructureType(null);
    setFile(null);
    setHeaders([]);
    setPreviewData([]);
    setMapping({});
    setImportId(null);
  };

  // Handle import completion
  const handleImportComplete = () => {
    const successMessage =
      importMode === 'bulk_images'
        ? getSuccessMessage('bulkImageCompleted')
        : getSuccessMessage('importCompleted');

    toast({
      title: successMessage,
      description: t('imports.success.importCompletedDesc', { count: 0 }), // Will be updated with actual count
    });
  };

  // Get step status for UI indicators
  const getStepStatus = (step: WizardStep) => {
    const stepOrder = ['mode', 'upload', 'mapping', 'uploading', 'progress'];
    const currentIndex = stepOrder.indexOf(currentStep);
    const stepIndex = stepOrder.indexOf(step);

    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'current';
    return 'upcoming';
  };

  // Get step icon based on status
  const getStepIcon = (step: WizardStep, status: string) => {
    if (status === 'completed') return CheckCircle;
    if (status === 'current') {
      switch (step) {
        case 'mode':
          return Settings;
        case 'upload':
          return Upload;
        case 'mapping':
          return Layers;
        case 'uploading':
          return Upload;
        case 'progress':
          return CheckCircle;
        default:
          return Info;
      }
    }
    return HelpCircle;
  };

  // Render step guidance based on current step
  const renderStepGuidance = () => {
    switch (currentStep) {
      case 'mode':
        return (
          <Alert className="mb-6">
            <Info className="size-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-medium">{getWizardText('guidance.mode', 'heading')}</p>
                <ul className="list-inside list-disc space-y-1 text-sm">
                  <li>{getWizardText('guidance.mode', 'products')}</li>
                  <li>{getWizardText('guidance.mode', 'structure')}</li>
                  <li>{getWizardText('guidance.mode', 'bulk_images')}</li>
                </ul>
                <p className="mt-2 text-xs text-muted-foreground">
                  {getWizardText('guidance.mode', 'note')}
                </p>
              </div>
            </AlertDescription>
          </Alert>
        );

      case 'upload':
        return (
          <Alert className="mb-6">
            <FileText className="size-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-medium">
                  {importMode === 'products' &&
                    getWizardText('guidance.upload.products', 'heading')}
                  {importMode === 'structure' &&
                    t('imports.wizard.guidance.upload.structure.heading', {
                      type: getWizardText(
                        'guidance.upload.structure',
                        structureType || 'attribute_groups'
                      ),
                    })}
                  {importMode === 'bulk_images' &&
                    getWizardText('guidance.upload.bulk_images', 'heading')}
                </p>
                <div className="space-y-1 text-sm">
                  <p>
                    <strong>{getWizardText('guidance.upload', 'supportedFormats')}</strong>{' '}
                    {importMode === 'bulk_images'
                      ? getWizardText('guidance.upload', 'supportedImageFormatsValue')
                      : getWizardText('guidance.upload', 'supportedFormatsValue')}
                  </p>
                  <p>
                    <strong>{getWizardText('guidance.upload', 'maximumFileSize')}</strong>{' '}
                    {importMode === 'bulk_images'
                      ? getWizardText('guidance.upload', 'maximumImageFileSizeValue')
                      : getWizardText('guidance.upload', 'maximumFileSizeValue')}
                  </p>
                  <p>
                    <strong>{getWizardText('guidance.upload', 'required')}</strong>{' '}
                    {importMode === 'bulk_images'
                      ? getWizardText('guidance.upload', 'requiredImageValue')
                      : getWizardText('guidance.upload', 'requiredValue')}
                  </p>
                </div>
                {importMode === 'products' && (
                  <div
                    className={`rounded border p-3 text-sm ${actualTheme === 'dark' ? 'border-gray-600 bg-gray-800 text-gray-200' : 'border-gray-200 bg-gray-50 text-gray-800'}`}
                  >
                    <p
                      className={`font-medium ${actualTheme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}
                    >
                      {getWizardText('guidance.upload.requiredColumns', 'heading')}
                    </p>
                    <ul
                      className={`list-inside list-disc ${actualTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
                    >
                      <li>
                        <strong>{getWizardText('guidance.upload.requiredColumns', 'sku')}</strong>{' '}
                        {getWizardText('guidance.upload.requiredColumns', 'skuDesc')}
                      </li>
                      <li>
                        <strong>{getWizardText('guidance.upload.requiredColumns', 'name')}</strong>{' '}
                        {getWizardText('guidance.upload.requiredColumns', 'nameDesc')}
                      </li>
                    </ul>
                  </div>
                )}
                {importMode === 'structure' && structureType && (
                  <div
                    className={`rounded border p-3 text-sm ${actualTheme === 'dark' ? 'border-gray-600 bg-gray-800 text-gray-200' : 'border-gray-200 bg-gray-50 text-gray-800'}`}
                  >
                    <p
                      className={`font-medium ${actualTheme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}
                    >
                      {getWizardText('guidance.upload.requiredColumns', structureType)}
                    </p>
                  </div>
                )}
              </div>
            </AlertDescription>
          </Alert>
        );

      case 'mapping':
        return (
          <Alert className="mb-6">
            <Layers className="size-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-medium">{getWizardText('guidance.mapping', 'heading')}</p>
                <div className="space-y-1 text-sm">
                  <p>
                    • <strong>{getWizardText('guidance.mapping', 'requiredFields')}</strong>
                  </p>
                  <p>
                    • <strong>{getWizardText('guidance.mapping', 'autoDetection')}</strong>
                  </p>
                  <p>
                    • <strong>{getWizardText('guidance.mapping', 'previewData')}</strong>
                  </p>
                </div>
                <div
                  className={`rounded border p-3 text-sm ${actualTheme === 'dark' ? 'border-gray-600 bg-gray-800 text-gray-200' : 'border-gray-200 bg-gray-50 text-gray-800'}`}
                >
                  <p
                    className={`font-medium ${actualTheme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}
                  >
                    {getWizardText('guidance.mapping', 'proTips')}
                  </p>
                  <ul
                    className={`mt-1 list-inside list-disc ${actualTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
                  >
                    <li>{getWizardText('guidance.mapping', 'tip1')}</li>
                    <li>{getWizardText('guidance.mapping', 'tip2')}</li>
                    <li>{getWizardText('guidance.mapping', 'tip3')}</li>
                  </ul>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        );

      case 'uploading':
        return (
          <Alert className="mb-6">
            <Upload className="size-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-medium">{getWizardText('guidance.uploading', 'heading')}</p>
                <p className="text-sm">{getWizardText('guidance.uploading', 'description')}</p>
              </div>
            </AlertDescription>
          </Alert>
        );

      case 'progress':
        return (
          <Alert className="mb-6">
            <CheckCircle className="size-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-medium">{getWizardText('guidance.processing', 'heading')}</p>
                <p className="text-sm">{getWizardText('guidance.processing', 'description')}</p>
              </div>
            </AlertDescription>
          </Alert>
        );

      default:
        return null;
    }
  };

  // Render the current step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'mode':
        return <StepImportMode onModeSelected={handleModeSelected} />;

      case 'upload':
        return (
          <StepUpload
            onFileSelected={handleFileSelected}
            onBulkImageSelected={handleBulkImageSelected}
            duplicatePolicy={duplicateStrategy}
            onDuplicatePolicyChange={handleDuplicateStrategyChange}
            importMode={importMode}
            structureType={structureType}
            onStructureTypeSelect={handleStructureTypeSelect}
          />
        );

      case 'mapping':
        return (
          <StepMapping
            sourceHeaders={headers}
            previewData={previewData}
            fullData={fullData}
            onMappingComplete={handleMappingComplete}
            importMode={importMode}
            structureType={structureType}
            duplicateStrategy={duplicateStrategy}
            fieldSchema={getCurrentFieldSchema()}
          />
        );

      case 'uploading':
        return (
          <UploadProgress
            fileName={file?.name || zipFile?.name || `${imageFiles?.length || 0} files`}
            fileSize={
              file?.size || zipFile?.size || imageFiles.reduce((total, img) => total + img.size, 0)
            }
            uploadProgress={uploadProgress}
            isUploading={isUploading}
          />
        );

      case 'progress':
        return (
          <StepProgress
            importId={importId || 0}
            onComplete={handleImportComplete}
            importType={
              importMode === 'products'
                ? 'products'
                : importMode === 'bulk_images'
                  ? 'bulk_images'
                  : structureType === 'attribute_groups'
                    ? 'attribute-groups'
                    : structureType || 'products'
            }
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <Card className="mx-auto w-full">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="size-6" />
                <span>{getImportTitle()}</span>
              </CardTitle>
              <CardDescription className="mt-2">{getImportDescription()}</CardDescription>
            </div>
            {currentStep !== 'mode' && (
              <Button
                variant="outline"
                onClick={handleGoBackToModeSelection}
                className={`flex items-center gap-2 ${actualTheme === 'dark' ? 'border-gray-600 bg-gray-800 text-gray-200 hover:bg-gray-700 hover:text-gray-100' : ''}`}
              >
                <ArrowLeft className="size-4" />
                {getButtonText('back')} {getButtonText('toModeSelection')}
              </Button>
            )}
          </div>

          {/* Step Progress Indicator */}
          <div className="mt-6">
            <div className="flex items-center justify-between">
              {(['mode', 'upload', 'mapping', 'uploading', 'progress'] as WizardStep[]).map(
                (step, index) => {
                  const status = getStepStatus(step);
                  const Icon = getStepIcon(step, status);

                  return (
                    <div key={step} className="flex items-center">
                      <div className="flex items-center">
                        <div
                          className={`
                        flex size-8 items-center justify-center rounded-full border-2 
                        ${status === 'completed' ? 'border-green-500 bg-green-100 text-green-700' : ''}
                        ${status === 'current' ? 'border-blue-500 bg-blue-100 text-blue-700' : ''}
                        ${status === 'upcoming' ? 'border-gray-300 bg-gray-100 text-gray-500' : ''}
                      `}
                        >
                          <Icon className="size-4" />
                        </div>
                        <div className="ml-2">
                          <div
                            className={`text-sm font-medium ${
                              status === 'current'
                                ? 'text-blue-700'
                                : status === 'completed'
                                  ? 'text-green-700'
                                  : 'text-gray-500'
                            }`}
                          >
                            {getWizardText('steps', step)}
                          </div>
                        </div>
                      </div>
                      {index < 4 && (
                        <div
                          className={`mx-4 h-0.5 flex-1 ${
                            status === 'completed' ? 'bg-green-300' : 'bg-gray-200'
                          }`}
                        />
                      )}
                    </div>
                  );
                }
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {renderStepGuidance()}

          <Tabs value={currentStep} className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="mode" disabled={currentStep !== 'mode'}>
                {getWizardText('steps', 'mode')}
              </TabsTrigger>
              <TabsTrigger value="upload" disabled={currentStep !== 'upload'}>
                {getWizardText('steps', 'upload')}
              </TabsTrigger>
              <TabsTrigger value="mapping" disabled={currentStep !== 'mapping'}>
                {getWizardText('steps', 'mapping')}
              </TabsTrigger>
              <TabsTrigger value="uploading" disabled={currentStep !== 'uploading'}>
                {getWizardText('steps', 'uploading')}
              </TabsTrigger>
              <TabsTrigger value="progress" disabled={currentStep !== 'progress'}>
                {getWizardText('steps', 'progress')}
              </TabsTrigger>
            </TabsList>

            <div className="mt-6">{renderCurrentStep()}</div>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default UploadWizard;
